/**
 * R-14 · El teléfono se normaliza en un solo sitio.
 *
 * Un móvil mexicano se escribe de dos formas y las dos son correctas:
 *
 *     +52 662 467 4951
 *     +52 1 662 467 4951      ← con el «1» que se usaba antes de 2019
 *
 * Las dos llegan al mismo aparato. Para una base de datos son dos cadenas
 * distintas — y como `customers.phone` es único global, sin normalizar se crean
 * **dos clientes para la misma persona**: sellos separados, historial partido, y
 * alguien en el mostrador diciendo «yo ya tenía siete». No falla ruidosamente.
 * Simplemente no reconoce.
 *
 * ── Por qué una biblioteca y no una expresión regular ────────────────────────
 * Los planes de numeración cambian —México cambió el suyo en 2019— y cada país
 * tiene los suyos. `libphonenumber-js` es la de Google, con los datos de todos
 * los países y mantenida. Escribir esto a mano es elegir mantener un problema
 * que ya está resuelto.
 *
 * ── Y por qué se normaliza ANTES de Supabase ─────────────────────────────────
 * La alternativa era averiguar qué forma guarda Supabase Auth y adaptarnos. Eso
 * nos ataría a una decisión que no controlamos y que pueden cambiar en cualquier
 * versión. Normalizando antes, **Supabase sólo ve una forma**: no puede crear
 * dos personas porque nunca conoce dos números.
 *
 * Sirve además donde no hay Supabase de por medio — el mostrador, cuando el
 * cajero teclea el número de un cliente para buscarlo.
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';
/** México. Lo que se asume cuando alguien escribe sin código de país. */
export const PAIS_POR_OMISION = 'MX';
/**
 * Formas heredadas que México dejó de usar en 2019 pero que **la gente sigue
 * escribiendo y las operadoras siguen entregando**. `libphonenumber-js` las
 * rechaza porque sigue el plan vigente, y tiene razón — pero rechazarlas aquí
 * significaría decirle a un cliente que su propio teléfono no es válido, y eso
 * parece un fallo nuestro.
 *
 * Medido el 2026-09-02: las cuatro dan «inválido» sin esta limpieza.
 *
 * Sólo se intenta **cuando el número no se pudo interpretar tal cual**, así que
 * nunca deforma uno que ya era correcto — un `+1 662 467 4951` de Estados
 * Unidos se interpreta como estadounidense y no llega hasta aquí.
 */
function limpiarFormasHeredadasMx(entrada) {
    const d = entrada.replace(/[^\d+]/g, '');
    //  +52 1 XXXXXXXXXX  →  el «1» de móvil de antes de 2019
    const conUno = d.match(/^\+?521(\d{10})$/);
    if (conUno)
        return `+52${conUno[1]}`;
    //  044 / 045 XXXXXXXXXX  →  marcación nacional vieja, local y celular
    const viejo = d.match(/^04[45](\d{10})$/);
    if (viejo)
        return `+52${viejo[1]}`;
    //  1 XXXXXXXXXX  →  el mismo «1», escrito sin código de país
    const nacional = d.match(/^1(\d{10})$/);
    if (nacional)
        return `+52${nacional[1]}`;
    return null;
}
export class TelefonoInvalido extends Error {
    entrada;
    constructor(entrada) {
        super(`«${entrada}» no es un número de teléfono válido`);
        this.entrada = entrada;
    }
}
/**
 * Devuelve el número en E.164, la única forma que el sistema guarda y consulta.
 * Lanza si no es válido — **antes** de gastar un SMS en él.
 *
 *   normalizar('+5216624674951')   → '+526624674951'
 *   normalizar('+52 662 467 4951') → '+526624674951'
 *   normalizar('6624674951')       → '+526624674951'
 */
export function normalizarTelefono(entrada, pais = PAIS_POR_OMISION) {
    const limpio = (entrada ?? '').trim();
    if (!limpio)
        throw new TelefonoInvalido(entrada);
    const directo = parsePhoneNumberFromString(limpio, pais);
    if (directo?.isValid())
        return directo.number;
    // Sólo si no se pudo interpretar tal cual. Ver `limpiarFormasHeredadasMx`.
    if (pais === 'MX') {
        const heredado = limpiarFormasHeredadasMx(limpio);
        if (heredado) {
            const reintento = parsePhoneNumberFromString(heredado, pais);
            if (reintento?.isValid())
                return reintento.number;
        }
    }
    throw new TelefonoInvalido(entrada);
}
/** Como el anterior, pero devuelve `null` en vez de lanzar. Para buscar. */
export function normalizarONulo(entrada, pais = PAIS_POR_OMISION) {
    try {
        return normalizarTelefono(entrada, pais);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=telefono.js.map